import { consoleRoutes, type TenantNavEntry, type TenantNavLink } from "@sagot/constants";
import { useMemo } from "react";
import { usePermissions } from "../../../session/permissions";
import { FIELD_NAV_ICON } from "./field-constants";

/** The sidebar link of the engineer screens. The server's nav has no item for it (it is a view of Field visits), so the console adds it. */
export const FIELD_NAV_LINK: TenantNavLink = {
  kind: "link",
  id: "my-day",
  label: "My day",
  i18nKey: "service.field.nav.entry",
  icon: FIELD_NAV_ICON,
  permission: "visits.perform",
  agentScoped: false,
  to: ({ ws }) => consoleRoutes.myDay(ws),
};

/**
 * Adds "My day" under "Field visits" when the server sent that link (so the Service flag, the plan's after-sales feature and the
 * visits module are all on, and it is not plan-locked) and the member may work visits (`visits.perform`, or `visits.manage`).
 * Pure so the rule can be tested without a session.
 */
export function withFieldNav(entries: readonly TenantNavEntry[], allowed: boolean): TenantNavEntry[] {
  const at = entries.findIndex((entry) => entry.kind === "link" && entry.id === "visits");
  const visits = entries[at];
  if (!allowed || at < 0 || visits?.kind !== "link" || visits.locked === true) return [...entries];
  return [...entries.slice(0, at + 1), FIELD_NAV_LINK, ...entries.slice(at + 1)];
}

export function useFieldNavEntries(entries: readonly TenantNavEntry[]): TenantNavEntry[] {
  const permissions = usePermissions();
  const allowed = permissions.can("visits.perform", { anywhere: true }) || permissions.can("visits.manage", { anywhere: true });
  return useMemo(() => withFieldNav(entries, allowed), [entries, allowed]);
}
